// "Add Device" — add a device that is already on the server by typing the
// Unique Number printed on it.
//
// ⚠️ ONLY A DEVICE THAT IS YOURS TO ADD: you own it or its location, you are a
// Super Admin, or nobody owns it yet. Someone else's device is shared with a QR
// (⋮ → Share (QR) on their phone or desktop, then Me → Add Shared Device —
// ScanShareScreen). See core/devices/claimDevice for why a printed number stopped
// being enough on 2026-10-11.
//
// A share code pasted here is handed to the scanner screen rather than refused.

import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, CircularProgress, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import { CenterTitleBar } from '../components/MobileAppBar';
import { useApp } from '../../../core/state/AppContext';
import { claimDevice } from '../../../core/devices/claimDevice';
import { findShareCode } from '../../../core/share/shareAccess';

export default function AddDeviceScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { userId, isSuperAdmin } = useApp();

  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!code.trim() || busy) return;
    const share = findShareCode(code);
    if (share) { navigate('/scan-share', { state: { code: share } }); return; }
    setBusy(true);
    setResult(null);
    try {
      setResult(await claimDevice(userId, code, { isSuperAdmin }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar title="Add Device" showBack />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          {result ? (
            <Box sx={{ textAlign: 'center', pt: 4 }}>
              {result.ok
                ? <CheckCircleIcon sx={{ fontSize: 72, color: '#4CAF50' }} />
                : <ErrorOutlineIcon sx={{ fontSize: 72, color: '#F44336' }} />}
              <Typography sx={{ color: c.text, fontSize: 20, fontWeight: 700, mt: 2 }}>
                {result.title}
              </Typography>
              <Typography sx={{ color: `${c.text}B3`, mt: 1.5 }}>{result.message}</Typography>
              <Button
                onClick={() => (result.ok ? navigate('/home?tab=1') : setResult(null))}
                sx={{
                  mt: 4, bgcolor: c.button, color: c.text, px: 3, py: 1.25,
                  borderRadius: '12px', textTransform: 'none',
                  '&:hover': { bgcolor: c.button },
                }}
              >
                {result.ok ? 'Done' : 'Try Again'}
              </Button>
            </Box>
          ) : (
            <>
              <Typography sx={{ color: `${c.text}B3`, fontSize: 14, mb: 2 }}>
                {'Enter the Unique Number printed on the device. It must already '
                  + 'be powered on and connected to Wi-Fi.'}
              </Typography>

              <TextField
                fullWidth
                autoFocus
                label="Unique Number"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                sx={{
                  '& .MuiInputLabel-root': { color: `${c.text}B3` },
                  '& .MuiOutlinedInput-root': {
                    color: c.text,
                    borderRadius: '12px',
                    '& fieldset': { borderColor: `${c.text}66` },
                    '&:hover fieldset': { borderColor: c.text },
                    '&.Mui-focused fieldset': { borderColor: c.text },
                  },
                }}
              />

              <Button
                fullWidth
                onClick={submit}
                disabled={busy || !code.trim()}
                sx={{
                  mt: 3, bgcolor: c.button, color: c.text, py: 1.5,
                  borderRadius: '12px', fontSize: 16, textTransform: 'none',
                  '&:hover': { bgcolor: c.button },
                }}
              >
                {busy ? <CircularProgress size={20} sx={{ color: c.text }} /> : 'Add Device'}
              </Button>

              <Typography sx={{ color: `${c.text}99`, fontSize: 13, mt: 3 }}>
                Setting up a brand-new device? Use “Set Up New Device” first to
                send it your Wi-Fi.
              </Typography>
              <Typography sx={{ color: `${c.text}99`, fontSize: 13, mt: 1.5 }}>
                Someone else&apos;s device? Ask them to share it with a QR, then use
                “Add Shared Device” on the Me tab.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
